<template>
  <section class="mc-modatech mc-container" aria-label="Рекомендации">
    <p v-if="message" class="mc-modatech__message" role="status">{{ message }}</p>
    <div ref="container" :id="containerId"></div>
  </section>
</template>

<script>
import axios from 'axios'
import { nextTick } from 'vue'
import { getModatechWidget, getModatechUserId } from '@/services/modatechSession'
import { configureModatechActions, syncModatechState, normalizeIds, cleanupModatechPage } from '@/services/modatechWidgets'

let sequence = 0
let identityReloading = false
export default {
  props: {
    type: {type: String, default: 'custom'},
    pageData: {type: Object, default: () => ({})},
    scrollPosition: {default: null}
  },
  data() {
    return {containerId: 'mcbuy-modatech-' + (++sequence), message: '', generation: 0}
  },
  computed: {
    userId() { return String(this.$store.getters.user?.id || '') },
    counterSignature() {
      return JSON.stringify([this.$store.getters.counterBasketData?.ids, this.$store.getters.counterFavoriteData?.ids])
    }
  },
  watch: {
    // Parent keys give every SKU/route configuration a separate, disposable DOM instance.
    counterSignature() { this.sync() },
    userId() { this.checkIdentity() }
  },
  mounted() { this.initialize() },
  beforeUnmount() {
    this.generation++
    try { this.widget?.closePopup?.() } catch (error) { /* SDK may be unavailable */ }
    this.scrollHost?.remove()
    if (this.stopWatching) { this.stopWatching() }
    cleanupModatechPage(this.containerId)
  },
  methods: {
    checkIdentity(widget = this.widget) {
      const previous = getModatechUserId()
      if (previous !== null && previous !== this.userId) {
        // The SDK has no reliable logout API: init('') leaves its old internal user_id.
        // Reload only on an actual identity change, after the app has updated its server session.
        cleanupModatechPage(this.containerId)
        if (!identityReloading) {
          identityReloading = true
          try { widget?.stopSession?.() } catch (error) { /* Reload still discards SDK memory. */ }
          const url = new URL(window.location.href)
          // Browser QA starts through ?dev=Y; keep it across the identity reload.
          if (!window.cordova) { url.searchParams.set('dev', 'Y') }
          window.location.replace(url.href)
        }
        return false
      }
      return true
    },
    async sync() {
      try {
        const widget = await getModatechWidget(axios)
        if (this.$refs.container?.isConnected && this.checkIdentity(widget)) {
          syncModatechState(widget, this.$store)
        }
      } catch (error) { /* External recommendations must not block the app. */ }
    },
    async initialize() {
      const generation = ++this.generation
      try {
        const [widget, counter] = await Promise.all([
          getModatechWidget(axios), this.$store.dispatch('fetchCounterData')
        ])
        await nextTick()
        const container = this.$refs.container
        if (generation !== this.generation || !container?.isConnected || !this.checkIdentity(widget)) { return }
        if (!counter) { return }
        this.widget = widget
        configureModatechActions(widget, this.$store, this.$router, message => { this.message = message }, this.containerId,
          this.type === 'product' ? document.getElementById('modatech-shop-the-model') : null)
        syncModatechState(widget, this.$store)
        const options = {...this.pageData}
        switch (this.type) {
          case 'main': widget.initMain(container); break
          case 'category': widget.initCategory(container, options); break
          case 'product':
            if (this.scrollPosition) {
              this.scrollHost = document.createElement('div')
              this.scrollPosition.appendChild(this.scrollHost)
              options.scroll_button = {position: this.scrollHost, caption: 'Идеи для образов', offset: -70}
            }
            widget.initProduct(container, options)
            break
          case 'cart':
            widget.initCart(container, {yml_ids: normalizeIds(this.$store.getters.counterBasketData?.ids)})
            break
          case 'favorites':
            widget.initFavorites(container, {product_ids: normalizeIds(this.$store.getters.counterFavoriteData?.ids).slice(0, 5)})
            break
          case 'inline': {
            const href = 'https://widget.modatech.ru/custom/inline.css'
            if (!document.querySelector('link[href="' + href + '"]')) {
              const link = document.createElement('link')
              link.rel = 'stylesheet'
              link.href = href
              document.head.appendChild(link)
            }
            widget.initInline(container, options)
            break
          }
          default: widget.initCustom(container, options)
        }
        // SDK has no destroy. Late responses can append a popup to body after unmount;
        // remove those by owned container ID, without touching the next screen.
        this.stopWatching = cleanupModatechPage(this.containerId, false)
      } catch (error) { /* Missing SDK/data leaves the normal screen fully usable. */ }
    }
  }
}
</script>

<style>
.mc-modatech { width: 100%; min-width: 0; margin-top: 24px; margin-bottom: 24px; }
.mc-modatech:has(> div:empty):not(:has(.mc-modatech__message)) { margin: 0; }
.mc-modatech__message { padding: 12px 0; }
.mc-modatech-scroll { position: absolute; top: 0; right: 0; width: 100px; height: 100px; z-index: 2; pointer-events: none; }
.mc-modatech-scroll .garderobo-scroll-button { max-width: 100%; pointer-events: auto; }
</style>
